import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc, setDoc, deleteDoc } from "firebase/firestore";
import { deleteUser, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import Navbar from "./Navbar";

function UserProfile() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) {
        navigate("/login");
        return;
      }

      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData(data);
          setDisplayName(data.displayName || currentUser.displayName || "");
        } else {
          // Create new user document
          const newUserData = {
            email: currentUser.email,
            displayName: currentUser.displayName || "",
            createdAt: new Date().toISOString(),
            completedLessons: [],
            vocabularyProgress: {},
            quizScores: [],
            studyStreak: 0,
            totalStudyTime: 0,
            preferences: {
              language: "en",
              notifications: true,
              theme: "korean"
            }
          };
          
          await setDoc(userRef, newUserData);
          setUserData(newUserData);
          setDisplayName(newUserData.displayName);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser, navigate]);

  const handleSaveProfile = async () => {
    try {
      if (!currentUser) return;
      const userRef = doc(db, "users", currentUser.uid);
      
      await updateDoc(userRef, {
        displayName: displayName,
        updatedAt: new Date().toISOString()
      });
      
      setUserData({ ...userData, displayName });
      setEditing(false);
      setError("");
    } catch (error) {
      console.error("Error updating profile:", error);
      setError("Failed to update profile");
    }
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
    setError("");
  };

  const confirmDeleteAccount = async () => {
    setDeleteLoading(true);
    setError("");

    try {
      if (!currentUser) return;

      // Check if user signed up with Google
      const isGoogleUser = currentUser.providerData.some(provider => provider.providerId === 'google.com');
      
      if (isGoogleUser) {
        // For Google users, we can delete directly without password
        const userRef = doc(db, "users", currentUser.uid);
        
        // Delete user document
        await deleteDoc(userRef);
        
        // Delete user account
        await deleteUser(currentUser);
      } else {
        // For email/password users, require password confirmation
        if (!deletePassword.trim()) {
          setError("Please enter your password");
          setDeleteLoading(false);
          return;
        }

        // Re-authenticate user with password
        const credential = EmailAuthProvider.credential(currentUser.email, deletePassword);
        await reauthenticateWithCredential(currentUser, credential);

        const userRef = doc(db, "users", currentUser.uid);
        
        // Delete user document
        await deleteDoc(userRef);
        
        // Delete user account
        await deleteUser(currentUser);
      }
      
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      if (error.code === "auth/wrong-password") {
        setError("Incorrect password");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError("Failed to delete account. Please try again.");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDeleteAccount = () => {
    setShowDeleteModal(false);
    setDeletePassword("");
    setError("");
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-100 to-red-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 shadow-2xl border-b-4 border-red-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
            <div className="text-center">
              <div className="flex justify-center mb-6 sm:mb-8">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-red-500 to-orange-500 rounded-full blur-lg opacity-30 animate-pulse"></div>
                  <div className="text-4xl sm:text-6xl relative z-10">👤</div>
                  <div className="absolute -top-2 -right-2 bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-full animate-bounce">
                    🇰🇷
                  </div>
                </div>
              </div>
              
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-black text-gray-900 mb-4 sm:mb-6">
                My Profile
              </h1>
              <div className="mb-4 sm:mb-6">
                <p className="text-xl sm:text-3xl text-red-700 font-black mb-2">내 프로필</p>
                <p className="text-sm sm:text-lg text-gray-600 font-semibold">(My Profile)</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Profile Information */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-10 border-4 border-red-200 mb-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-black text-gray-900">Profile Information</h2>
              <button
                onClick={() => setEditing(!editing)}
                className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-6 py-3 rounded-2xl font-bold hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {editing ? "Cancel" : "Edit Profile"}
              </button>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label className="block text-lg font-bold text-gray-800 mb-3">Email</label>
                <input
                  type="email"
                  value={userData?.email || ""}
                  disabled
                  className="w-full px-6 py-4 border-4 border-gray-200 rounded-2xl bg-gray-100 text-gray-600 text-lg font-semibold"
                />
                <p className="text-sm text-gray-500 mt-2">Email cannot be changed</p>
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-800 mb-3">Display Name</label>
                {editing ? (
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full px-6 py-4 border-4 border-red-200 rounded-2xl focus:ring-4 focus:ring-red-300 focus:border-red-400 transition-colors duration-300 text-gray-900 placeholder-gray-500 text-lg font-semibold bg-white/80"
                      placeholder="Enter your display name"
                    />
                    <button
                      onClick={handleSaveProfile}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      Save Changes
                    </button>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={displayName || "Not set"}
                    disabled
                    className="w-full px-6 py-4 border-4 border-gray-200 rounded-2xl bg-gray-100 text-gray-600 text-lg font-semibold"
                  />
                )}
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-800 mb-3">Member Since</label>
                <input
                  type="text"
                  value={userData?.createdAt ? new Date(userData.createdAt).toLocaleDateString() : "Unknown"}
                  disabled
                  className="w-full px-6 py-4 border-4 border-gray-200 rounded-2xl bg-gray-100 text-gray-600 text-lg font-semibold"
                />
              </div>
            </div>
          </div>

          {/* Learning Progress */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl shadow-2xl p-10 border-4 border-green-200 mb-8">
            <h2 className="text-3xl font-black text-gray-900 mb-8">Learning Progress</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl font-black text-green-600 mb-3">
                  {userData?.completedLessons?.length || 0}
                </div>
                <p className="text-gray-800 font-bold text-lg">Lessons Completed</p>
                <p className="text-gray-600 font-semibold">out of 20 total</p>
              </div>
              
              <div className="text-center">
                <div className="text-5xl font-black text-orange-600 mb-3">
                  {userData?.studyStreak || 0}
                </div>
                <p className="text-gray-800 font-bold text-lg">Day Streak</p>
                <p className="text-gray-600 font-semibold">Keep it up!</p>
              </div>
              
              <div className="text-center">
                <div className="text-5xl font-black text-blue-600 mb-3">
                  {userData?.totalStudyTime || 0}
                </div>
                <p className="text-gray-800 font-bold text-lg">Minutes Studied</p>
                <p className="text-gray-600 font-semibold">Total time</p>
              </div>
            </div>
          </div>

          {/* Account Actions */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-2xl p-10 border-4 border-red-200">
            <h2 className="text-3xl font-black text-gray-900 mb-8">Account Actions</h2>
            
            <div className="space-y-6">
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-red-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                📊 View Dashboard
              </button>
              
              <button
                onClick={() => navigate("/lessons")}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                📚 Continue Learning
              </button>
              
              <div className="border-t-2 border-red-200 pt-6">
                <button
                  onClick={handleDeleteAccount}
                  className="w-full bg-gradient-to-r from-red-700 to-red-800 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-red-800 hover:to-red-900 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  🗑️ Delete Account
                </button>
                <p className="text-sm text-gray-600 mt-2 text-center">
                  This will permanently delete your account and all progress
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Account Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">Delete Account</h2>
                <p className="text-gray-600 font-semibold">
                  This action cannot be undone. All your progress will be permanently lost.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {/* Show password field only for email/password users */}
              {currentUser && !currentUser.providerData.some(provider => provider.providerId === 'google.com') && (
                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-800 mb-2">
                    Enter your password to confirm:
                  </label>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-300"
                    placeholder="Your password"
                    disabled={deleteLoading}
                  />
                </div>
              )}

              {/* Show different message for Google users */}
              {currentUser && currentUser.providerData.some(provider => provider.providerId === 'google.com') && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-blue-800 text-sm font-medium">
                    Since you signed up with Google, no password confirmation is needed. Click "Delete Account" to proceed.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={cancelDeleteAccount}
                  disabled={deleteLoading}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-3 rounded-xl font-bold hover:bg-gray-300 transition-colors duration-300 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAccount}
                  disabled={deleteLoading || (currentUser && !currentUser.providerData.some(provider => provider.providerId === 'google.com') && !deletePassword.trim())}
                  className="flex-1 bg-red-600 text-white px-4 py-3 rounded-xl font-bold hover:bg-red-700 transition-colors duration-300 disabled:opacity-50"
                >
                  {deleteLoading ? "Deleting..." : "Delete Account"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default UserProfile;
